import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonProperties = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Encapsulation & @property: The Gatekeepers</h1>
                <p className="subtitle">How to protect your data without breaking your API. The Pythonic alternative to Getters and Setters.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine your Object is a <strong>VIP Nightclub</strong>.
                    </p>
                    <ul>
                        <li><strong>Public Attribute (`obj.age = 5`):</strong> There are no doors. Anyone can walk in. A toddler (age 5) can enter. This is dangerous!</li>
                        <li><strong>Getter/Setter Methods (`set_age(5)`):</strong> You install a door and hire a Bouncer. The Bouncer checks IDs. If age &lt; 18, he says "No!". But now, everyone has to learn a new way to enter (`set_age` instead of just walking in).</li>
                        <li><strong>@property (The Smart Door):</strong> It <em>looks</em> like an open door (`obj.age = 21`). But when you walk through, a <strong>Holographic Bouncer</strong> instantly appears and checks your ID. If you are cool, you pass. If not, you bounce. The interface didn't change, but the security did!</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Problem with Public Attributes</h2>
                <p>
                    <p>
                        In Python, we usually start with direct attribute access because it's clean and simple.
                        However, problems arise when you need to add validation logic later.
                        Imagine you have a `User` class where anyone can set the age.
                    </p>
                </p>
                <CodeBlock language="python" code={`class User:
    def __init__(self, username, age):
        self.username = username
        self.age = age # Public!

u = User("Mario", 25)
u.age = -99 #  Physics violation! But Python allows it.`} />
                <p>
                    When you realize "Oh no, I need to prevent negative ages!", your instinct (from Java/C++) is to rewrite everything using `get_age()` and `set_age()`.
                </p>
                <ProTip><p><strong>The Breaking Change:</strong> If you change `u.age` to `u.set_age()`, you break the code of <strong>everyone</strong> who was using your class. They all have to find/replace their code. This is bad library design.</p></ProTip>
                <p>
                    This is why Java developers write getters and setters from Day 1, "just in case".
                    In Python, we don't need to do that. We have a better tool.
                </p>
            </div>

            <div className="content-section">
                <h2>3. The Solution: @property</h2>
                <p>
                    The `@property` decorator allows you to add logic to attribute access <strong>backward compatibly</strong>.
                    You keep the `u.age` syntax, but Python runs a function behind the scenes.
                    This is known as "<strong>Uniform Access Principle</strong>"the user shouldn't care if they are accessing a variable or calling a function.
                </p>

                <CodeBlock language="python" code={`class User:
    def __init__(self, username, age):
        self.username = username
        self._age = age # Note the underscore (Internal storage)

    # 1. The Getter
    @property
    def age(self):
        return self._age

    # 2. The Setter
    @age.setter
    def age(self, new_age):
        if new_age < 0:
            print(" Error: Age cannot be negative!")
            return # Rejects the change
        self._age = new_age
        print(f"Age updated to {self._age}")

u = User("Luigi", 30)

# Looks like a variable...
u.age = 40     # Age updated to 40 (Calls the Setter)
print(u.age)   # 40 (Calls the Getter)

# But behaves like a function!
u.age = -5     #  Error: Age cannot be negative!`} />
            </div>

            <div className="content-section">
                <h2>4. Computed Properties</h2>
                <p>
                    Properties aren't just for validation. They are perfect for values that differ based on other values.
                    Why store `area` in memory if you can calculate it from `width` and `height`?
                </p>

                <CodeBlock language="python" code={`class Rectangle:
    def __init__(self, width, height):
        self.width = width
        self.height = height

    @property
    def area(self):
        # Calculated on the fly!
        return self.width * self.height

r = Rectangle(5, 10)
print(r.area) # 50

r.width = 10
print(r.area) # 100 (Updates automatically!)`} />

                <p>
                    If we had stored `self.area` in `__init__`, it would have stayed `50` even after we changed the width. Computed properties ensure <strong>Data Consistency</strong>.
                </p>
            </div>

            <div className="content-section">
                <h2>5. Deep Dive: Read-Only Attributes</h2>
                <p>
                    Want to create a value that can be read but NEVER changed?
                    Just define a getter <strong>without</strong> a setter.
                </p>
                <CodeBlock language="python" code={`class Circle:
    def __init__(self, radius):
        self.radius = radius

    @property
    def pi(self):
        return 3.14159

c = Circle(5)
print(c.pi) # 3.14159

# c.pi = 3 #  AttributeError: can't set attribute`} />
                <p>
                    This is extremely useful for constants, IDs, or derived data that should be immutable.
                </p>
            </div>

            <div className="content-section">
                <h2>6. Advanced: Cached Properties</h2>
                <p>
                    What if calculating the property is expensive? (e.g., Calling a database).
                    You don't want to run it every time `obj.attr` is accessed.
                    We can cache the result!
                </p>

                <CodeBlock language="python" code={`from functools import cached_property # Python 3.8+
import time
import { ProTip } from '../../components/LessonUI';

class DataSet:
    def __init__(self, data):
        self.data = data

    @cached_property
    def standard_deviation(self):
        print("Complex Math in progress...")
        time.sleep(2) # Fake heavy work
        return sum(self.data) / len(self.data) # Simplification

ds = DataSet([1, 2, 3, 4, 5])

# First access: Runs the code
print(ds.standard_deviation) 
# Output: "Complex Math in progress...", then 3.0

# Second access: Instant! (Returns cached value)
print(ds.standard_deviation) 
# Output: 3.0`} />
                <ProTip><p><strong>How it works:</strong> The first time you access the property, it replaces itself in the object's `__dict__` with the calculated value. Future lookups find the value directly in the dictionary, bypassing the method call entirely!</p></ProTip>
            </div>

            <div className="content-section">
                <h2>7. Internal Mechanics: The Descriptor Protocol</h2>
                <p>
                    How does `@property` actually work? It uses the <strong>Descriptor Protocol</strong>.
                    In Python, any object that defines `__get__`, `__set__`, or `__delete__` is a Descriptor.
                </p>
                <p>
                    When you write `x = obj.age`, Python looks up `age` in the class. It sees that `age` is a Descriptor object (specifically, `property` is a class that implements `__get__`).
                    Python then yields control to `age.__get__(obj)`.
                    This is the magic glue that powers properties, methods, `staticmethod`, and `classmethod`.
                </p>
                <p>
                    Normally, `obj.attribute` just looks for a value in `obj.__dict__`. Descriptors intercept this lookup, allowing you to run code <em>during attribute access</em>.
                </p>
            </div>

            <div className="content-section">
                <h2>8. Refactoring Challenge: The Bank Account</h2>
                <p>
                    Let's refactor a bad class into a "Gold Standard" class using properties.
                </p>
                <CodeBlock language="python" code={`# The Bad Class
class BankAccount:
    def __init__(self, balance):
        self.balance = balance # Unsafe!

# The Good Class
class SecureAccount:
    def __init__(self, balance):
        self._balance = max(0, balance) # Initial validation

    @property
    def balance(self):
        return f"\${self._balance:.2f}" # Formatting logic in Getter

    @balance.setter
    def balance(self, value):
        if value < 0:
            print(" Insufficient funds!")
        else:
            self._balance = value
            print("Balance updated")

acc = SecureAccount(100)
print(acc.balance) # "$100.00"

acc.balance = -50  #  Insufficient funds!
print(acc.balance) # "$100.00" (Still safe)`} />
            </div>



        </div>
    );
};

export default PythonProperties;

